import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createSession } from '../services/apiService';
import { useToast } from '../components/common/Toast';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { extractSessionId } from '../utils/validation';

export function HomePage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [sessionId, setSessionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreateSession = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await createSession();
      showToast('Session created successfully!', 'success');
      navigate(`/session/${response.id}`, {
        state: { facilitatorToken: response.facilitatorToken },
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create session';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      setLoading(false);
    }
  };

  const handleJoinSession = () => {
    if (!sessionId.trim()) {
      const errorMessage = 'Please enter a session ID or join URL';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      return;
    }
    const extractedId = extractSessionId(sessionId);
    navigate(`/session/${extractedId}`);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Planning Poker
        </h1>
        <p className="text-lg text-gray-600">
          Estimate user stories together with your team
        </p>
      </div>

      <div className="space-y-6">
        <Card>
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">
              Start New Session
            </h2>
            <p className="text-gray-600 mb-6">
              Create a new planning poker session and invite your team
            </p>
            <Button
              variant="primary"
              size="lg"
              onClick={handleCreateSession}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              {loading ? 'Creating...' : 'Start New Session'}
            </Button>
          </div>
        </Card>

        <Card>
          <div>
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">
              Join Existing Session
            </h2>
            <p className="text-gray-600 mb-4">
              Enter a session ID or join URL to join an existing planning poker session
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={sessionId}
                onChange={(e) => {
                  setSessionId(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleJoinSession();
                  }
                }}
                placeholder="Enter Session ID or Join URL"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <Button
                variant="secondary"
                onClick={handleJoinSession}
                className="w-full sm:w-auto"
              >
                Join
              </Button>
            </div>
            {error && (
              <p className="mt-3 text-sm text-red-600">{error}</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

